"use client";

import Image from "next/image";
import { Video, Armchair, Clock } from "lucide-react";
import { ServiceTheme } from "./BookingStepper";
import { getBusinessTimezoneDisplay } from "@/lib/timezone";

export interface BookingSummaryProps {
  sessionType: "online" | "in-person";
  supportOption: string;
  duration: string;
  price: number;
  appointmentDate?: Date | null;
  appointmentTime?: string | null;
  practitionerName?: string | null;
  practitionerImage?: string | null;
  serviceTheme?: ServiceTheme;
}

// Brand colors matching BookingStepper theme colors
const themeColors: Record<ServiceTheme, string> = {
  counseling: "#896deb",
  lifeCoaching: "#DDA500",
  occupationalTherapy: "#C01610",
  psychology: "#C01610",
};

// Default blue theme (used when no service is selected yet)
const defaultThemeColor = "#006DFF";

const hexToRgba = (hex: string, alpha: number) => {
  const value = parseInt(hex.replace("#", ""), 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const BookingSummary = ({
  sessionType,
  supportOption,
  duration,
  price,
  appointmentDate,
  appointmentTime,
  practitionerName,
  practitionerImage,
  serviceTheme,
}: BookingSummaryProps) => {
  const themeColor = serviceTheme ? themeColors[serviceTheme] : defaultThemeColor;
  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = (time: string) => {
    const existingAmpm = time.match(/\s*(AM|PM)$/i);
    if (existingAmpm) {
      const cleaned = time.replace(/\s*(AM|PM)$/i, "").trim();
      const [hours, minutes] = cleaned.split(":");
      const hour = parseInt(hours);
      const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      return `${displayHour}:${minutes} ${existingAmpm[1].toUpperCase()}`;
    }
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const SessionIcon = sessionType === "online" ? Video : Armchair;

  return (
    <div className="flex flex-col">
      <h3 className="p-2.5 font-onest text-2xl font-semibold text-black">
        Booking Summary
      </h3>

      {/* Session Type Badge */}
      <div className="p-2.5">
        <div
          className="inline-flex items-center gap-2.5 rounded-[20px] border-4 border-white px-5 py-2.5"
          style={{
            backgroundColor: themeColor,
            boxShadow: "inset 0px 0px 20px 0px rgba(255,255,255,0.5)",
          }}
        >
          <SessionIcon className="size-6 text-white" />
          <span className="font-onest font-medium text-white">
            {sessionType === "online" ? "Online Session" : "In-Person Session"}
          </span>
        </div>
      </div>

      {/* Support Option */}
      <div className="p-2.5">
        <div className="border-l-2 pl-2.5" style={{ borderColor: themeColor }}>
          <div className="p-2.5">
            <span className="font-onest text-base font-semibold text-black">
              {supportOption}
            </span>
          </div>
          <div className="flex items-center gap-7 p-2.5">
            <div className="flex items-center gap-1.5">
              <Clock className="size-6" style={{ color: themeColor }} />
              <span className="font-onest text-xl font-medium" style={{ color: themeColor }}>
                {duration}
              </span>
            </div>
            <span className="font-onest text-xl font-bold text-black/60">
              ${price}
            </span>
          </div>
        </div>
      </div>

      {/* Appointment Details */}
      <div className="rounded-[10px] bg-v3-primary/10 p-2.5">
        <div className="p-2.5">
          <span className="font-onest text-base font-semibold text-black">
            Appointment
          </span>
        </div>
        <div className="flex gap-2.5 px-2.5">
          <span className="font-onest text-base font-normal text-black/60">
            {appointmentDate ? formatDate(appointmentDate) : "Select a date"}
          </span>
          <span className="font-onest text-base font-normal text-black/60">
            {appointmentTime ? formatTime(appointmentTime) : "Select a time"}
          </span>
        </div>
        <div className="px-2.5 pb-2.5">
          <span className="font-onest text-xs font-normal text-black/30">
            {getBusinessTimezoneDisplay(appointmentDate)}
          </span>
        </div>
      </div>

      {/* Practitioner (if selected) */}
      {practitionerName && (
        <div className="p-2.5">
          <div className="p-2.5">
            <span className="font-onest text-base font-semibold text-black">
              Practitioner
            </span>
          </div>
          <div className="flex items-center gap-2.5 px-2.5">
            <div className="relative size-10 overflow-hidden rounded-full border-2" style={{ borderColor: hexToRgba(themeColor, 0.2) }}>
              <Image
                src={practitionerImage || "/images/v3/booking/practionerSelection/practitioner-placeholder.jpg"}
                alt={practitionerName}
                fill
                className="object-cover"
                sizes="40px"
              />
            </div>
            <span className="font-onest text-base font-normal text-black/60">
              {practitionerName}
            </span>
          </div>
        </div>
      )}

      {/* Total Cost */}
      <div className="border-b border-black/20 p-2.5">
        <div className="p-2.5">
          <span className="font-onest text-base font-semibold text-black">
            Total Cost
          </span>
        </div>
        <div className="flex items-center justify-between p-2.5">
          <span className="font-onest text-xl font-medium text-black/60">
            Session Fee
          </span>
          <span className="font-onest text-xl font-normal text-black/60">
            ${price} AUD
          </span>
        </div>
      </div>

      {/* Total */}
      <div className="p-2.5">
        <div className="flex items-center justify-between p-2.5">
          <span className="font-onest text-xl font-medium text-black/60">
            Total
          </span>
          <span className="font-onest text-xl font-normal text-black/60">
            ${price} AUD
          </span>
        </div>
      </div>
    </div>
  );
};

export default BookingSummary;
